// frontend/src/App.js
// Main application entry point — sets up providers, theme, notifications and routes.

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
// Ant Design core app, theme and notification utilities
import { App as AntApp, ConfigProvider, message } from 'antd';
// Authentication context to manage user sessions
import { AuthProvider } from './context/AuthContext';

import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import Students from './pages/StudentsPage';
import Attendance from './pages/AttendancePage';
import MarksPage from './pages/MarksPage';
import ClassManagementPage from './pages/ClassManagementPage';
import SingleClassManagementPage from './pages/SingleClassManagementPage';
import UserManagementPage from './pages/UserManagementPage';
import StudentProfilePage from './pages/StudentProfilePage';
import AssignmentsPage from './pages/AssignmentsPage';
import ClassSubjectsPage from './pages/ClassSubjectsPage';
import SubjectManagementPage from './pages/SubjectManagementPage';
import ExamManagementPage from './pages/ExamManagementPage';
import CalendarPage from './pages/CalendarPage';
import { AcademicYearProvider } from './context/AcademicYearContext';
import AcademicYearManagementPage from './pages/AcademicYearManagementPage';
import StudentFormPage from './pages/StudentFormPage';
import { NotificationProvider } from './context/NotificationContext';
import AdmissionPage from './pages/AdmissionPage'; // Public page for admissions
import InquiryListPage from './pages/InquiryListPage'; // Admin inquiries list
import AuditLogPage from './pages/AuditLogPage';
import PromotionPathsPage from './pages/PromotionPathsPage';
import ProfilePage from './pages/ProfilePage';
import AppUpdateNotification from './components/AppUpdateNotification';
import ReportsPage from './pages/ReportsPage';
import TutorFeedbackDashboard from './pages/TutorFeedbackDashboard';
// Report pages
import StudentMarkSheetPage from './pages/reports/StudentMarkSheetPage';
import ClassMarkReportPage from './pages/reports/ClassMarkReportPage';
import ExamMarkReportPage from './pages/reports/ExamMarkReportPage';
import MarkRangeReportPage from './pages/reports/MarkRangeReportPage';
import ClassAttendanceReportPage from './pages/reports/ClassAttendanceReportPage';
import DemographicsReportPage from './pages/reports/DemographicsReportPage';
import BPLStudentsReportPage from './pages/reports/BPLStudentsReportPage';
import SCSTStudentsReportPage from './pages/reports/SCSTStudentsReportPage';
import StudentPrintReportPage from './pages/reports/StudentPrintReportPage';

import { ThemeProvider, useTheme } from './context/ThemeContext';
import SettingsPage from './pages/SettingsPage';


// Configure global message behavior (Ant Design notifications)
// maxCount: limit concurrent messages, duration: default seconds
message.config({
  maxCount: 3,
  duration: 3,
});

// Wrapper component to consume ThemeContext
const AppContent = () => {
  const { themeConfig } = useTheme();

  return (
    <ConfigProvider theme={themeConfig}>
      <AntApp>
        <NotificationProvider>
          {/* Small helper to expose PWA install prompt when available */}
          <AppUpdateNotification />
          <Router>
            <Routes>
              {/* Public Routes: accessible without authentication */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admission" element={<AdmissionPage />} />

              {/* Protected Routes: requires authenticated user.
                  The path "/*" catches any nested route not matched above
                  and delegates to ProtectedRoute for access control. */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    {/* MainLayout provides the app chrome (sidebar, header) */}
                    <MainLayout>
                      <Routes>
                        {/* Dashboard — landing page for logged-in users */}
                        <Route path="/" element={<Dashboard />} />

                        {/* Student-related routes */}
                        <Route path="/students" element={<Students />} />
                        <Route path="/students/class/:classId" element={<Students />} />
                        <Route path="/students/add" element={<StudentFormPage />} />
                        <Route path="/students/edit/:id" element={<StudentFormPage />} />
                        <Route path="/students/:id" element={<StudentProfilePage />} />

                        {/* Attendance, Marks and other academic features */}
                        <Route path="/attendance" element={<Attendance />} />
                        <Route path="/marks" element={<MarksPage />} />
                        <Route path="/classes" element={<ClassManagementPage />} />
                        <Route path="/classes/:id" element={<SingleClassManagementPage />} />
                        <Route path="/users" element={<UserManagementPage />} />
                        <Route path="/assignments" element={<AssignmentsPage />} />
                        <Route path="/class-subjects" element={<ClassSubjectsPage />} />
                        <Route path="/subjects" element={<SubjectManagementPage />} />
                        <Route path="/exams" element={<ExamManagementPage />} />
                        <Route path="/calendar" element={<CalendarPage />} />
                        <Route path="/academic-years" element={<AcademicYearManagementPage />} />
                        <Route path="/inquiries" element={<InquiryListPage status="pending" />} />
                        <Route path="/inquiries/rejected" element={<InquiryListPage status="rejected" />} />
                        <Route path="/logs" element={<AuditLogPage />} />
                        <Route path="/promotion-paths" element={<PromotionPathsPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/feedback-dashboard" element={<TutorFeedbackDashboard />} />

                        {/* Report routes */}
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/reports/student-marks" element={<StudentMarkSheetPage />} />
                        <Route path="/reports/class-marks" element={<ClassMarkReportPage />} />
                        <Route path="/reports/exam-marks" element={<ExamMarkReportPage />} />
                        <Route path="/reports/marks-range" element={<MarkRangeReportPage />} />
                        <Route path="/reports/class-attendance" element={<ClassAttendanceReportPage />} />
                        <Route path="/reports/gender" element={<DemographicsReportPage />} />
                        <Route path="/reports/bpl-students" element={<BPLStudentsReportPage />} />
                        <Route path="/reports/scst-students" element={<SCSTStudentsReportPage />} />
                        <Route path="/reports/student-printing" element={<StudentPrintReportPage />} />
                        {/* Add other protected routes inside this nested Routes component */}
                      </Routes>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </NotificationProvider>
      </AntApp>
    </ConfigProvider>
  );
};

function App() {
  // Root component: wraps the app with providers (Auth, AcademicYear, Notifications)
  // and configures theming for Ant Design via ConfigProvider.
  return (
    <AuthProvider>
      <AcademicYearProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AcademicYearProvider>
    </AuthProvider>
  );
}

export default App;